const absoluteUrlPattern = /^[a-z][a-z\d+\-.]*:\/\//i;

function ensureLeadingSlash(value: string): string {
  return value.startsWith("/") ? value : `/${value}`;
}

function normalizePublicPath(value: string): string {
  const path = value === "/" ? "" : ensureLeadingSlash(value);
  return path.endsWith("/") ? path.slice(0, -1) : path;
}

export const getPublicPath = (): string => {
  const publicUrl = process.env.PUBLIC_URL?.trim();

  if (!publicUrl || publicUrl === ".") {
    return "";
  }

  if (absoluteUrlPattern.test(publicUrl)) {
    try {
      return normalizePublicPath(new URL(publicUrl).pathname);
    } catch {
      return "";
    }
  }

  return normalizePublicPath(publicUrl);
};

export const toPublicUrl = (path = ""): string => {
  if (absoluteUrlPattern.test(path)) {
    return path;
  }

  const publicPath = getPublicPath();
  if (!path) {
    return publicPath || "/";
  }

  return `${publicPath}${ensureLeadingSlash(path)}`;
};

export const toAbsolutePublicUrl = (path = ""): string => {
  if (absoluteUrlPattern.test(path)) {
    return path;
  }

  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";

  return new URL(toPublicUrl(path), origin).toString();
};

export const getCurrentPublicUrl = (): string => {
  if (typeof window === "undefined") {
    return toAbsolutePublicUrl("/");
  }

  const currentUrl = new URL(window.location.href);
  const publicPath = getPublicPath();

  if (
    publicPath &&
    currentUrl.pathname !== publicPath &&
    !currentUrl.pathname.startsWith(`${publicPath}/`)
  ) {
    currentUrl.pathname = `${publicPath}${ensureLeadingSlash(
      currentUrl.pathname,
    )}`;
  }

  return currentUrl.toString();
};

export const toAppPathname = (path: string): string => {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";

  let pathname: string;
  try {
    pathname = new URL(path, origin).pathname;
  } catch {
    pathname = ensureLeadingSlash(path);
  }

  const publicPath = getPublicPath();
  if (
    publicPath &&
    (pathname === publicPath || pathname.startsWith(`${publicPath}/`))
  ) {
    const appPath = pathname.slice(publicPath.length);
    return appPath || "/";
  }

  return ensureLeadingSlash(pathname);
};
